import type { CapabilityDefinitionCatalogReadPort } from '../definition/adapters/capability-definition-catalog-port';
import { CatalogWireError } from '../definition/adapters/capability-definition-catalog-wire';
import { catalogQuery, type CapabilityDefinitionCatalogReadResultV1 } from './catalog-contract';
export interface CatalogReadState {
  readonly requestId: number;
  readonly phase: 'NOT_LOADED' | 'LOADING' | 'RESULT' | 'TRANSPORT_ERROR' | 'WIRE_INVALID';
  readonly result: CapabilityDefinitionCatalogReadResultV1 | null;
}
export function initialCatalogReadState(): CatalogReadState {
  return { requestId: 0, phase: 'NOT_LOADED', result: null };
}
export function createCatalogReadSession(
  port: CapabilityDefinitionCatalogReadPort,
  changed: (state: CatalogReadState) => void,
) {
  let state = initialCatalogReadState();
  let sequence = 0;
  let disposed = false;
  let controller: AbortController | null = null;
  function publish(next: CatalogReadState) {
    state = next;
    if (!disposed) changed(next);
  }
  return {
    async load(): Promise<void> {
      if (disposed) return;
      controller?.abort();
      const active = new AbortController();
      controller = active;
      const requestId = ++sequence;
      publish({ requestId, phase: 'LOADING', result: null });
      try {
        const result = await port.read(catalogQuery(), active.signal);
        if (!disposed && sequence === requestId && !active.signal.aborted)
          publish({ requestId, phase: 'RESULT', result });
      } catch (error) {
        if (!disposed && sequence === requestId && !active.signal.aborted)
          publish({
            requestId,
            phase: error instanceof CatalogWireError ? 'WIRE_INVALID' : 'TRANSPORT_ERROR',
            result: null,
          });
      }
    },
    cancel() {
      if (disposed) return;
      controller?.abort();
      publish({ requestId: ++sequence, phase: 'NOT_LOADED', result: null });
    },
    dispose() {
      disposed = true;
      ++sequence;
      controller?.abort();
    },
    current() {
      return state;
    },
  };
}
